import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { auth0Audience } from '../../api/API';
import { Student } from '../../api/models/Student';
import { Environment } from '../../Environment';
import { RootState } from '../../model/store';
import { studentSlice } from '../../model/studentSlice';
import { Auth0Context, useAuth0 } from '../Auth0Provider';

export type StudentListProps = {
  // inputs
  /** list of students */
  students: Student[];
  /** type of student sorting */
  studentSort: 'student-asc' | 'student-dsc';
  /** type of grade sorting */
  gradeSort: 'grade-asc' | 'grade-dsc';
  /** type of gpa sorting */
  gpaSort: 'gpa-asc' | 'gpa-dsc';
  /** if currently getting students */
  isGettingStudents: boolean;
  /** search term */
  searchTerm: string;

  // outputs
  /** invoked if user clicks either grade/student sorting */
  userClickedSort: (type: 'student-asc' | 'student-dsc' | 'grade-asc' | 'grade-dsc' | 'gpa-asc' | 'gpa-dsc') => void;
  /** invoked if user initiates search */
  userSubmittedSearchTerm: (term: string) => void;
  /** invoked if user changes search term */
  userChangedTerm: (searchTerm: string) => void;
  /** as the name implies */
  userClickedResetList: () => void;
};

export const studentListContainer = (StudentListScreen: React.ComponentType<StudentListProps>) => () => {
  const students = useSelector((state: RootState) => state.student.list);
  const filteredStudents = useSelector((state: RootState) => state.student.filteredList);
  const dispatch = useDispatch();

  // TODO: persist sorting, refactor implementation
  const [studentSort, setStudentSort] = React.useState<'student-asc' | 'student-dsc'>('student-dsc');
  const [gradeSort, setGradeSort] = React.useState<'grade-asc' | 'grade-dsc'>('grade-dsc');
  const [gpaSort, setGPASort] = React.useState<'gpa-asc' | 'gpa-dsc'>('gpa-dsc');
  const [isGettingStudents, setIsGettingStudents] = React.useState(false);
  const [term, setTerm] = React.useState('');
  const [list, setList] = React.useState<Student[]>(filteredStudents.length ? filteredStudents : students);

  const { api } = Environment.current();
  const { isAuthenticated, getTokenSilently } = useAuth0() as Auth0Context;

  const _resetList = () => {
    dispatch(studentSlice.actions.receivedFilteredList({ filteredList: [] }));
    setList(students);
  };

  // To prevent updates on unmount
  const isMounted = useRef(true);
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (isAuthenticated) {
      (async () => {
        try {
          setIsGettingStudents(true);
          const token = await getTokenSilently({
            audience: auth0Audience,
            scope: 'admin:students',
          });

          const list = await api.getStudents(token || '');
          if (isMounted.current) {
            setIsGettingStudents(false);
            dispatch(studentSlice.actions.receivedList({ list }));
            setList(list);
          }
        } catch (e) {
          toast.error(e.message);
          setIsGettingStudents(false);
        }
      })();
    }

    return () => {
      _resetList();
    };
  }, [isAuthenticated]);

  return (
    <StudentListScreen
      searchTerm={term}
      isGettingStudents={isGettingStudents}
      students={list}
      studentSort={studentSort}
      gradeSort={gradeSort}
      gpaSort={gpaSort}
      userChangedTerm={setTerm}
      userClickedResetList={() => {
        _resetList();
        setTerm('');
      }}
      userSubmittedSearchTerm={async text => {
        try {
          if (!text) {
            _resetList();
            return;
          }

          if (!isAuthenticated) throw new Error("You're not authenticated!");

          setIsGettingStudents(true);
          const token = await getTokenSilently({ audience: auth0Audience, scope: 'admin:students' });
          const filteredList = await api.filterStudents(text, token || '');

          if (isMounted.current) {
            setIsGettingStudents(false);
            dispatch(studentSlice.actions.receivedFilteredList({ filteredList }));
            setList(filteredList);
          }
        } catch (e) {
          toast.error(e.message);
          setIsGettingStudents(false);
        }
      }}
      userClickedSort={type => {
        switch (type) {
          case 'student-dsc':
            setStudentSort(type);
            const sortedStudents = [...list];
            sortedStudents.sort((a, b) => {
              if (a.lastName.toUpperCase() < b.lastName.toUpperCase()) return -1;
              if (a.lastName.toUpperCase() > b.lastName.toUpperCase()) return 1;
              return 0;
            });
            setList(sortedStudents);
            break;
          case 'student-asc':
            setStudentSort(type);
            const dscStudents = [...list];
            dscStudents.sort((a, b) => {
              if (a.lastName.toUpperCase() > b.lastName.toUpperCase()) return -1;
              if (a.lastName.toUpperCase() < b.lastName.toUpperCase()) return 1;
              return 0;
            });
            setList(dscStudents);
            break;
          case 'grade-dsc':
            setGradeSort(type);
            const ascGradeStudents = [...list];
            ascGradeStudents.sort((a, b) => a.grade - b.grade);
            setList(ascGradeStudents);
            break;
          case 'grade-asc':
            setGradeSort(type);
            const dscGradeStudents = [...list];
            dscGradeStudents.sort((a, b) => b.grade - a.grade);
            setList(dscGradeStudents);
            break;
          case 'gpa-dsc':
            setGPASort(type);
            const ascGPAStudents = [...list];
            ascGPAStudents.sort((a, b) => a.gpa - b.gpa);
            setList(ascGPAStudents);
            break;
          case 'gpa-asc':
            setGPASort(type);
            const dscGPAStudents = [...list];
            dscGPAStudents.sort((a, b) => b.gpa - a.gpa);
            setList(dscGPAStudents);
            break;
        }
      }}
    />
  );
};
